<template>
  <header :class="[isSticky ? 'sticky' : 'fixed', 'left-0 top-0 z-50 w-full py-4']">
    <div class="container relative z-30 flex w-full items-center justify-end">
      <div class="flex gap-x-4">
        <StarButton
          :tabindex="0"
          class="transition-transform duration-300"
          :class="isNavOpen && 'rotate-[360deg]'"
          @click="toggleNav"
          @keydown.enter="toggleNav"
        />
        <RouterLink v-if="hasBackButton" :to="{ name: 'FanzoneIndex' }">
          <Button size="sm" variant="secondary">
            <ArrowLeftFromLine class="h-8 w-8 cursor-pointer" />
          </Button>
        </RouterLink>
      </div>
    </div>
    <div class="fixed left-0 top-0 h-dvh w-screen transition-all" :class="overlayClasses">
      <div class="container flex h-full w-full flex-col items-end justify-between px-4 pb-8 pt-20">
        <div class="grid w-max justify-end gap-4 text-white">
          <template v-for="(item, index) in navItems" :key="item.id">
            <router-link
              v-if="!item.isExternal"
              :to="item.href"
              class="text-right text-3xl hover:underline"
              :class="[
                isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
                'transition-all duration-300 ease-out',
              ]"
              :style="{ 'transition-delay': `${index * 0.1}s` }"
              active-class="text-primary"
              @click="item?.closeOnClick && closeNav()"
            >
              {{ item.label }}
            </router-link>
            <a
              v-else
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="text-right text-3xl hover:underline"
              :class="[
                isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
                'transition-all duration-300 ease-out',
              ]"
              :style="{ 'transition-delay': `${index * 0.1}s` }"
              @click="trackExternalLink(item.href)"
            >
              {{ item.label }}
            </a>
          </template>
          <span
            :tabindex="0"
            class="cursor-pointer text-right text-3xl text-white hover:underline"
            :class="[
              isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
              'transition-all duration-300 ease-out',
            ]"
            :style="{ 'transition-delay': `${navItems.length * 0.1}s` }"
            :label="t('common.share')"
            @click="startShare"
            @keydown.enter="startShare"
          >
            {{ t('common.share') }}
          </span>
          <span
            :tabindex="0"
            class="cursor-pointer text-right text-3xl text-white hover:underline"
            :class="[
              isNavOpen ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0',
              'transition-all duration-300 ease-out',
            ]"
            :style="{ 'transition-delay': `${(navItems.length + 1) * 0.1}s` }"
            :label="t('common.logout')"
            @click="accountStore.logoutFan(true)"
            @keydown.enter="accountStore.logoutFan(true)"
          >
            {{ t('common.logout') }}
          </span>
        </div>
      </div>
      <div class="absolute left-0 top-0 -z-10 h-dvh w-screen" @click="closeNav"></div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScrollLock } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useShare } from '@vueuse/core';
import { useAccountStore } from '@stores/account.store';
import { ArrowLeftFromLine } from 'lucide-vue-next';

// Components
import Button from '@ui/button/Button.vue';
import StarButton from '@modules/Buttons/StarButton.vue';
import { useTelemetryFetcher } from '@api/tracking.api';

defineProps({
  hasBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { share } = useShare();
const route = useRoute();
const accountStore = useAccountStore();
const isLocked = useScrollLock(document);

const navItems = ref([
  {
    id: 1,
    label: t('common.fanzone'),
    href: '/fanzone',
    isExternal: false,
    closeOnClick: true,
  },
  {
    id: 2,
    label: t('common.account'),
    href: '/profile',
    isExternal: false,
    closeOnClick: true,
  },
  {
    id: 3,
    label: t('common.officialStore'),
    href: 'https://lanadelrey.lnk.to/oceanblvd',
    isExternal: true,
  },
]);
const isNavOpen = ref(false);

const isSticky = computed(() => {
  switch (route.name) {
    case 'FanzoneIndex':
    case 'FanzonePost':
      return true;
    default:
      return false;
  }
});
const overlayClasses = computed(() =>
  isNavOpen.value
    ? 'bg-black/70 pointer-events-auto backdrop-blur-lg opacity-100'
    : 'pointer-events-none opacity-0',
);

const toggleNav = () => {
  isNavOpen.value = !isNavOpen.value;
  isLocked.value = !isLocked.value;
};
const closeNav = () => {
  isNavOpen.value = false;
  isLocked.value = false;
};
const startShare = async () => {
  try {
    await share({
      title: 'Sparkle Jump Rope Queen',
      text: 'Lana del Rey',
      url: import.meta.env.VITE_HOME_URL,
    });
    useTelemetryFetcher().execute({
      metric: 'share',
      resource: window.location.href,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      // Share was cancelled by user, we can handle this case if needed
      console.log('Share was cancelled');
    } else {
      // Handle other potential errors
      console.error('Error sharing:', error);
    }
  }
};

const trackExternalLink = (href: string) => {
  useTelemetryFetcher().execute({
    metric: 'click-link',
    resource: href,
  });
};

watch(
  () => route.fullPath,
  () => {
    if (isNavOpen.value) {
      closeNav();
    }
  },
);
</script>
